import React, { useState } from 'react';
import { collection, doc, addDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { sanitizeString } from '../../utils/sanitization';
import { logEntityChanges } from '../../utils/logger';
import { useAuth } from '../auth/AuthContext';
import { generateLogId, generateSectionId } from '../../utils/documentIdUtils';
import { hasPermission, FEATURE_PERMISSIONS } from '../../utils/permissions';

/**
 * SupportPageSectionEditor - Modal form for creating/editing FAQ sections
 * 
 * Features:
 * - Create new section or edit existing one
 * - Icon/emoji picker (simple text input)
 * - Permission selector
 * - Display order management
 * - Active/inactive toggle
 * 
 * Props:
 * - initialSection: Existing section object (null for new section)
 * - onClose: Function to call when closing modal
 * - onSaved: Function to call after successful save (with success message)
 */
const SupportPageSectionEditor = ({ initialSection = null, onClose, onSaved }) => {
  const { currentUser } = useAuth();
  const isEditing = Boolean(initialSection?.id);
  
  // Form state
  const [sectionId, setSectionId] = useState(initialSection?.id || '');
  const [title, setTitle] = useState(initialSection?.title || '');
  const [icon, setIcon] = useState(initialSection?.icon || '📁');
  const [requiredPermission, setRequiredPermission] = useState(initialSection?.requiredPermission || '');
  const [order, setOrder] = useState(initialSection?.order || 0);
  const [isActive, setIsActive] = useState(initialSection?.isActive ?? true);
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Common permission options
  const permissionOptions = [
    { value: '', label: 'None (Public)' },
    { value: 'page:customers', label: 'Customers' },
    { value: 'page:contacts', label: 'Contacts' },
    { value: 'page:opportunities', label: 'Opportunities' },
    { value: 'page:tasks', label: 'Tasks' },
    { value: 'page:orders', label: 'Orders' },
    { value: 'page:reports', label: 'Reports' },
    { value: 'page:email_campaigns', label: 'Email Campaigns' },
    { value: 'page:user_admin', label: 'User Admin' }
  ];
  
  // Common emoji options for sections
  const iconSuggestions = ['🚀', '👥', '📞', '💼', '✅', '📦', '📊', '📧', '⚙️', '💡', '🔧', '📁', '🎯', '⭐'];
  
  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission(currentUser, FEATURE_PERMISSIONS.KNOWLEDGE_MANAGE)) return;
    
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (!isEditing && !sectionId.trim()) {
      setError('Section ID is required for new sections');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      // Build section data
      const sectionData = {
        title: sanitizeString(title, 200),
        icon: sanitizeString(icon, 10),
        requiredPermission: requiredPermission || null,
        order: parseInt(order) || 0,
        isActive,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.displayName || 'Unknown'
      };
      
      if (isEditing) {
        // Update existing section
        const docRef = doc(db, collectionPath('supportPageSections'), initialSection.id);
        await updateDoc(docRef, sectionData);
        
        // Log changes
        await logEntityChanges({
          oldObj: initialSection,
          newObj: { ...initialSection, ...sectionData },
          fields: ['title', 'icon', 'requiredPermission', 'order', 'isActive'],
          entityType: 'SupportPageSection',
          entityId: initialSection.id,
          user: currentUser,
          action: 'Updated'
        });
        
        onSaved('Section updated successfully');
      } else {
        // Create new section with custom ID (or generated from title)
        const finalSectionId = sectionId.trim() || generateSectionId(title);
        const docRef = doc(db, collectionPath('supportPageSections'), finalSectionId);
        
        await setDoc(docRef, {
          id: finalSectionId,
          ...sectionData,
          createdAt: serverTimestamp(),
          createdBy: currentUser?.displayName || 'Unknown'
        });
        
        // Log creation
        const logId = generateLogId('SupportPageSection', finalSectionId, 'created');
        await addDoc(collection(db, collectionPath('logs')), {
          id: logId,
          timestamp: serverTimestamp(),
          level: 'info',
          category: 'entity_change',
          action: 'Created',
          entityType: 'SupportPageSection',
          entityId: finalSectionId,
          userId: currentUser?.uid || 'system',
          userEmail: currentUser?.email || 'system',
          userName: currentUser?.displayName || 'System',
          changes: [],
          metadata: { title: sectionData.title }
        });
        
        onSaved('Section created successfully');
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving section:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Section' : 'Create New Section'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Close"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          
          {/* Section ID (only for new sections) */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Section ID <span className="text-red-500">*</span>
                <span className="text-gray-500 text-xs ml-2">(lowercase, hyphens, no spaces)</span>
              </label>
              <input
                type="text"
                value={sectionId}
                onChange={(e) => setSectionId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., getting-started"
                pattern="[a-z0-9-]+"
                required
              />
            </div>
          )}
          
          {/* Section ID (read-only for editing) */}
          {isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Section ID
              </label>
              <input
                type="text"
                value={initialSection.id}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                disabled
              />
              <p className="text-xs text-gray-500 mt-1">Section ID cannot be changed after creation</p>
            </div>
          )}
          
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Getting Started"
              required
            />
          </div>
          
          {/* Icon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Icon/Emoji
            </label>
            <div className="space-y-2">
              <input
                type="text"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-2xl"
                placeholder="📁"
                maxLength={10}
              />
              <div className="flex flex-wrap gap-2">
                {iconSuggestions.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setIcon(emoji)}
                    className={`text-2xl px-3 py-2 rounded border ${
                      icon === emoji 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    title={`Use ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Required Permission */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Required Permission
            </label>
            <select
              value={requiredPermission}
              onChange={(e) => setRequiredPermission(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {permissionOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              If set, only users with this permission can see this section
            </p>
          </div>
          
          {/* Order and Active Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Order
              </label>
              <input
                type="number"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="0"
              />
            </div>
            
            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Active</span>
              </label>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : (isEditing ? 'Update Section' : 'Create Section')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportPageSectionEditor;
